import raw from './catalog.json' with { type: 'json' }

export interface Product { id: string; name: string; style: string; photo: string }
export interface Center { id: string; name: string }
export interface Catalog { products: Product[]; centers: Center[] }

/** Column widths for the catalog values copied onto an order. */
export const CATALOG_ID_LENGTH = 50
export const CATALOG_TEXT_LENGTH = 100

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const str = (v: unknown): string => (typeof v === 'string' ? v : '')
const blank = (v: unknown): boolean => str(v).trim() === ''

function tooLong(p: string[], kind: string, id: string, field: string, value: string, max: number): void {
  if (value.length > max) p.push(`${kind} '${id}' ${field} is longer than ${max} characters.`)
}

/** Collects every problem and reports them in one Error (same rules and order as the .NET CatalogLoader). */
export function checkCatalog(input: unknown): Catalog {
  if (!isObj(input)) throw new Error('catalog.json is invalid:\n- The file is empty.')
  const p: string[] = []
  const products: unknown[] = Array.isArray(input.products) ? input.products : []
  const centers: unknown[] = Array.isArray(input.centers) ? input.centers : []

  if (products.length === 0) p.push('No products.')
  if (centers.length === 0) p.push('No centers.')

  if (products.some((x) => !isObj(x))) p.push('A product entry is empty.')
  if (centers.some((x) => !isObj(x))) p.push('A center entry is empty.')
  const realProducts = products.filter(isObj)
  const realCenters = centers.filter(isObj)

  const seen = new Map<string, number>()
  for (const x of [...realProducts, ...realCenters]) {
    if (blank(x.id)) continue
    const id = str(x.id)
    seen.set(id, (seen.get(id) ?? 0) + 1)
  }
  for (const [id, n] of seen) if (n > 1) p.push(`Duplicate id '${id}'.`)

  for (const x of realProducts) {
    const id = str(x.id)
    if (blank(x.id)) p.push('A product has no id.')
    if (blank(x.name)) p.push(`Product '${id}' has no name.`)
    if (blank(x.style)) p.push(`Product '${id}' has no style.`)
    if (blank(x.photo)) p.push(`Product '${id}' has no photo.`)
    tooLong(p, 'Product', id, 'id', id, CATALOG_ID_LENGTH)
    tooLong(p, 'Product', id, 'name', str(x.name), CATALOG_TEXT_LENGTH)
    tooLong(p, 'Product', id, 'style', str(x.style), CATALOG_TEXT_LENGTH)
  }
  for (const x of realCenters) {
    const id = str(x.id)
    if (blank(x.id)) p.push('A center has no id.')
    tooLong(p, 'Center', id, 'id', id, CATALOG_ID_LENGTH)
    tooLong(p, 'Center', id, 'name', str(x.name), CATALOG_TEXT_LENGTH)
  }

  if (p.length > 0) throw new Error('catalog.json is invalid:\n- ' + p.join('\n- '))
  return input as unknown as Catalog
}

export const catalog: Catalog = checkCatalog(structuredClone(raw))

export function findProduct(c: Catalog, id: string): Product | undefined {
  return c.products.find((x) => x.id === id)
}

export function findCenter(c: Catalog, id: string): Center | undefined {
  return c.centers.find((x) => x.id === id)
}
